import { useEffect, useState } from 'react'
import { SkeletonList } from '../componentes/Skeleton'
import { EstadoError } from '../componentes/EstadoError'
import { EstadoVacio } from '../componentes/EstadoVacio'
import { EnlaceWhatsApp } from '../componentes/EnlaceWhatsApp'
import { IconoActualizar } from '../componentes/iconos'
import {
  useGrandesEmpresas,
  type FilaGE,
  type OrdenGE,
} from '../hooks/useGrandesEmpresas'
import { ModalAgendarGE } from './ge/ModalAgendarGE'
import { ModalRecurrenciaGE } from './ge/ModalRecurrenciaGE'
import { PerfilCliente } from '../componentes/PerfilCliente'

/**
 * Sección Grandes Empresas (B.4 · B.10).
 *
 * Lista los clientes con etiqueta GE en tarjetas (opción B): nombre, CIF,
 * población, último reparto y recurrencia (7/14/28 días) con pills de un clic.
 * Los GE NO reciben WhatsApp: su gestión es manual desde aquí. El "+ Pedido"
 * (catálogo sin bonos) queda como fase 2.
 */

/** Chip de la recurrencia actual del GE. */
function ChipRecurrencia({ dias }: { dias: number | null }) {
  if (dias === null) {
    return <span className="shrink-0 rounded-full border border-borde px-2.5 py-0.5 text-xs font-semibold text-tinta-apagada">sin fijar</span>
  }
  return <span className="shrink-0 rounded-full border border-acento-borde bg-acento-suave px-2.5 py-0.5 text-xs font-semibold text-acento-tinta">cada {dias} días</span>
}

/** Fecha ISO (YYYY-MM-DD) → "20 sep 2026". */
function fechaCorta(iso: string | null): string {
  if (!iso) return '—'
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })
}

function TarjetaGE({
  ge,
  onRecurrencia,
  onAgendar,
  onVerPerfil,
}: {
  ge: FilaGE
  onRecurrencia: () => void
  onAgendar: () => void
  onVerPerfil: () => void
}) {
  return (
    <li className="flex flex-col gap-3 rounded-xl border border-borde bg-superficie p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={onVerPerfil}
            className="block w-full max-w-full truncate text-left text-sm font-bold text-tinta underline-offset-2 hover:text-acento-tinta hover:underline"
            title={ge.nombre}
          >
            {ge.nombre}
          </button>
          <p className="truncate text-xs text-tinta-apagada">CIF {ge.cif || '—'}</p>
        </div>
        <ChipRecurrencia dias={ge.recurrenciaDias} />
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
        <div>
          <span className="block text-tinta-apagada">Población</span>
          <span className="text-tinta-2">{ge.poblacion || '—'}</span>
        </div>
        <div>
          <span className="block text-tinta-apagada">Último reparto</span>
          <span className="text-tinta-2">{fechaCorta(ge.ultimoReparto)}</span>
        </div>
        {ge.telefono ? (
          <div>
            <span className="block text-tinta-apagada">Teléfono</span>
            <EnlaceWhatsApp
              telefono={ge.telefono}
              etiqueta={ge.telefono}
              className="text-acento-tinta underline underline-offset-2 hover:text-acento-fuerte"
            />
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-tour="ge-recurrencia"
          onClick={onRecurrencia}
          className="rounded-lg border border-acento-borde bg-acento-suave px-3 py-1.5 text-xs font-semibold text-acento-tinta hover:brightness-110"
        >
          {ge.recurrenciaDias ? 'Editar recurrencia' : 'Configurar recurrencia'}
        </button>
        <button
          type="button"
          onClick={onAgendar}
          className="rounded-lg border border-borde-fuerte bg-superficie px-3 py-1.5 text-xs font-semibold text-tinta-2 hover:bg-elevado"
        >
          + Agendar reparto
        </button>
      </div>
    </li>
  )
}

export function GrandesEmpresas() {
  const [orden, setOrden] = useState<OrdenGE>('ultimo_reparto')
  const [busqueda, setBusqueda] = useState('')
  const [busquedaDebounced, setBusquedaDebounced] = useState('')
  const [agendando, setAgendando] = useState<FilaGE | null>(null)
  const [configurando, setConfigurando] = useState<FilaGE | null>(null)
  const [perfilId, setPerfilId] = useState<string | null>(null)
  const ge = useGrandesEmpresas(orden, busquedaDebounced)
  const items = ge.data?.items ?? []

  // Debounce de la búsqueda: no se lanza una petición por cada tecla.
  useEffect(() => {
    const t = setTimeout(() => setBusquedaDebounced(busqueda), 300)
    return () => clearTimeout(t)
  }, [busqueda])

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-tinta">Grandes Empresas</h1>
          <p className="mt-1 text-sm text-tinta-apagada">
            {ge.data ? `${ge.data.total} clientes GE. ` : ''}
            No reciben WhatsApp: se gestionan a mano desde aquí. Fija su recurrencia para
            preposicionarlos en el calendario.
          </p>
        </div>
        <button
          type="button"
          aria-label="Actualizar"
          onClick={() => void ge.refetch()}
          className="flex items-center gap-2 rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado"
        >
          <IconoActualizar className="size-4" />
          <span className="hidden sm:inline">Actualizar</span>
        </button>
      </div>

      {/* Búsqueda + orden */}
      <div data-tour="ge-buscar" className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar por nombre, CIF o población…"
          aria-label="Buscar grandes empresas"
          className="min-w-52 flex-1 rounded-lg border border-borde-fuerte bg-superficie px-3 py-1.5 text-sm text-tinta placeholder:text-tinta-apagada"
        />
        {([
          ['ultimo_reparto', 'Por último reparto'],
          ['poblacion', 'Por población'],
        ] as [OrdenGE, string][]).map(([clave, txt]) => (
          <button
            key={clave}
            type="button"
            onClick={() => setOrden(clave)}
            aria-pressed={orden === clave}
            className={[
              'rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
              orden === clave ? 'bg-acento text-tinta-inversa' : 'border border-borde-fuerte text-tinta-2 hover:bg-elevado',
            ].join(' ')}
          >
            {txt}
          </button>
        ))}
      </div>

      {ge.isPending ? (
        <SkeletonList />
      ) : ge.isError ? (
        <EstadoError error={ge.error} onReintentar={() => void ge.refetch()} />
      ) : items.length === 0 ? (
        <EstadoVacio
          titulo={busquedaDebounced ? 'Sin resultados' : 'Sin clientes GE'}
          descripcion={
            busquedaDebounced
              ? `Ningún GE coincide con "${busquedaDebounced}".`
              : 'No hay clientes con la etiqueta de Gran Empresa.'
          }
        />
      ) : (
        <ul data-tour="ge-lista" className="grid gap-3 sm:grid-cols-2">
          {items.map((g) => (
            <TarjetaGE
              key={g.contactId}
              ge={g}
              onRecurrencia={() => setConfigurando(g)}
              onAgendar={() => setAgendando(g)}
              onVerPerfil={() => setPerfilId(g.contactId)}
            />
          ))}
        </ul>
      )}

      {agendando ? (
        <ModalAgendarGE ge={agendando} onCerrar={() => setAgendando(null)} />
      ) : null}

      {configurando ? (
        <ModalRecurrenciaGE ge={configurando} onCerrar={() => setConfigurando(null)} />
      ) : null}

      {/* Perfil 360º del GE, abierto al pulsar su nombre. */}
      <PerfilCliente contactId={perfilId} onCerrar={() => setPerfilId(null)} />
    </div>
  )
}
